import cx from "classnames";
import get from "lodash/get";
import map from "lodash/map";
import startsWith from "lodash/startsWith";
import { useTranslation } from "react-i18next";
import { STRIPE } from "../constants";
import { useRillContext } from "../rill/RillReact/share/base";
import { getUserAccess } from "../utils/organization-helpers";
import {
  calculateFreeTrialDaysLeft,
  isPullActiveCoach,
  isPushOrPerformClient,
} from "../utils/user-helpers";
import { getRoleNameById } from "../views/dashboard/settings/manage-coaches/utils/manage-coaches-utils";

const SETTINGS_PAGE_KEYS = {
  "Account Settings": "accountSettings",
  Preferences: "preferences",
  Branding: "branding",
  "Manage Coaches": "manageCoaches",
  Notifications: "notifications",
  FAQS: "faqs",
  "Gym Contract": "gymContract",
  "Gym Location": "gymLocation",
  "Upgrade Account": "upgradeAccount",
  "Manage Billing": "manageBilling",
  "Manage Subscription": "manageSubscription",
  "Manage Coach": "manageCoach",
  Billing: "billing",
  "Coach Billing": "coachBilling",
  "Exercise Library": "exerciseLibrary",
  "Your Products": "yourProducts",
  "Billing Information": "billingInformation",
};

function SettingsNav(props) {
  const { t } = useTranslation("dashboard");
  const {
    locals: {
      pathname,
      activeUser,
      organizationRoles = [],
      organizationPermissions = [],
      organizationOwner,
      activeUserToRequest,
    },
  } = useRillContext();

  const isAthlete =
    !activeUser.permissions?.includes("coach") && !activeUser.pushAccountInfo;

  //Get the name of the organization's role of the current user
  const roleName = activeUser.organizationRoleId
    ? getRoleNameById(organizationRoles, activeUser.organizationRoleId)
    : null;

  //Hide options if the user doesn't have a Owner role into the organization
  const hideContentForOrganizationsRolesBelowToOwner =
    !!activeUser.organizationRoleId && roleName !== "Owner";
  //Hide options if the user doesn't have a Owner or Admin role into the organization.
  //Team Coach sees the same general settings pages as Admin (read-only — enforced
  //server-side, not here), it just never sees billing (covered by the Owner-only
  //flag above, which already excludes Team Coach).
  const hideContentForOrganizationsRolesBelowToAdmin =
    !!activeUser.organizationRoleId &&
    roleName !== "Admin" &&
    roleName !== "Owner" &&
    roleName !== "Team Coach";

  const PAGES = {
    "Account Settings": { url: "settings/profile", group: "general" },
    Preferences: { url: "settings/preferences", group: "general" },
    Branding: { url: "settings/branding", group: "general" },
    "Manage Coaches": { url: "settings/manage-coaches", group: "general" },
    FAQS: { url: "settings/faqs", group: "general" },
    "Gym Contract": { url: "settings/gym-contract", group: "gym" },
    "Gym Location": { url: "settings/gym-locations", group: "gym" },
    "Upgrade Account": { url: "settings/subscription", group: "general" },
    "Billing Information": {
      url: "settings/coach-manage-billing",
      group: "billing",
    },
    "Manage Subscription": { url: "settings/subscription", group: "billing" },
    "Manage Coach": { url: "settings/manage-coaching", group: "general" },
    Billing: { url: "settings/billing", group: "billing" },
    Notifications: { url: "notifications", group: "general" },
    ...(isAthlete
      ? {
          "Exercise Library": {
            url: "settings/exercise-library?primary=squat",
            group: "general",
          },
        }
      : {}),
  };

  const activeCoach = get(activeUser, "coach");
  const prospectiveCoach = get(activeUser, "coachingInvite.coach");
  const coach = activeCoach || prospectiveCoach;
  if (!coach) {
    delete PAGES["Manage Coach"];
  }
  const userIsCoach = activeUser.permissions?.includes("coach");
  //We are checking if the user is member of an organization, if it's check the owner user, if not, check the current user
  const userIsFe =
    (!activeUser.organizationId && !organizationOwner) || !userIsCoach
      ? Boolean(activeUser.pushAccountInfo)
      : Boolean(organizationOwner.pushAccountInfo);
  const userIsPressCoach =
    (!activeUser.organizationId && !organizationOwner) || !userIsCoach
      ? Boolean(activeUser.pressAccountInfo)
      : Boolean(organizationOwner.pressAccountInfo);
  const userIsSubscriber = isPushOrPerformClient(activeUser);
  const plan = get(activeUser, "stripe.plan");
  const PLANS = STRIPE.PLANS;
  const userIsMember = Boolean(activeUser.membership);

  if (
    userIsCoach ||
    activeUser.admin ||
    activeUser.ambassador ||
    plan === PLANS.LAUNCH.trial ||
    plan === PLANS.V2.trial ||
    activeUser.cancelled ||
    activeCoach
  ) {
    delete PAGES.Billing;
  }
  if (userIsCoach) {
    delete PAGES["Upgrade Account"];
    // Manage Subscription is the legacy athlete (v2 standard/premium) plan page.
    // Coaches, PUSH included, manage billing via Billing Information / Your Products.
    delete PAGES["Manage Subscription"];
    if (!userIsFe) {
      delete PAGES.FAQS;
    }
  }

  if (!userIsCoach) {
    delete PAGES.FAQS;
    delete PAGES["Manage Coaches"];
    delete PAGES["Manage Subscription"];
  }

  if (userIsSubscriber) {
    delete PAGES["Manage Coach"];
    delete PAGES.Notifications;
    delete PAGES["Manage Subscription"];
    delete PAGES.Billing;
  }
  if (userIsMember) {
    delete PAGES["Manage Coach"];
    delete PAGES["Manage Subscription"];
  }
  if (!userIsCoach && !userIsFe) {
    delete PAGES["Billing Information"];
    delete PAGES.Branding;
  }
  if (plan === PLANS.LAUNCH.trial || plan === PLANS.V2.trial) {
    delete PAGES["Manage Subscription"];
  }
  if ((plan !== PLANS.LAUNCH.trial && plan !== PLANS.V2.trial) || activeCoach) {
    delete PAGES["Upgrade Account"];
  }
  if (!userIsPressCoach) {
    delete PAGES["Gym Contract"];
    delete PAGES["Gym Location"];
  }

  if (hideContentForOrganizationsRolesBelowToOwner) {
    delete PAGES["Upgrade Account"];
    delete PAGES["Billing Information"];
    delete PAGES["Manage Subscription"];
    delete PAGES.Billing;
    delete PAGES["Manage Coach"];
  }

  if (hideContentForOrganizationsRolesBelowToAdmin) {
    delete PAGES["Upgrade Account"];
    delete PAGES["Billing Information"];
    delete PAGES["Manage Subscription"];
    delete PAGES.Billing;
    delete PAGES["Manage Coach"];
    delete PAGES.FAQS;
    delete PAGES.Branding;
    delete PAGES["Gym Contract"];
    delete PAGES["Gym Location"];
    delete PAGES["Manage Coaches"];
  }

  const daysLeft = calculateFreeTrialDaysLeft(activeUserToRequest);
  const isPullCoachOnTrial =
    activeUserToRequest.coachingFreeTrial && daysLeft > 0;
  const isPullCoach = isPullActiveCoach(activeUserToRequest);
  const { userCanAccessToPerform } = getUserAccess(
    activeUser,
    organizationOwner,
    organizationPermissions,
    organizationRoles
  );
  const userIsPerformCoach = userCanAccessToPerform;
  const isPushCoachWithEndedTrial =
    activeUserToRequest.permissions?.includes("coach") &&
    activeUserToRequest.pushAccountInfo &&
    daysLeft < 1 &&
    !isPullActiveCoach(activeUserToRequest) &&
    !userCanAccessToPerform;

  const PRODUCTS = [
    {
      id: "push",
      name: "PUSH",
      icon: "PU",
      description: "Subscription",
      isActive: userIsFe,
    },
    {
      id: "pull",
      name: "PULL",
      icon: "PL",
      description: "Coaching",
      isActive: isPullCoach,
    },
    {
      id: "press",
      name: "PRESS",
      icon: "PR",
      description: "Gym management",
      isActive: userIsPressCoach,
    },
    {
      id: "perform",
      name: "PERFORM",
      icon: "PE",
      description: "All in one",
      isActive: userIsPerformCoach,
    },
  ];

  const basePath = startsWith(pathname, "/coaching-portal/")
    ? "/coaching-portal/"
    : "/dashboard/";
  const isCoachingPortal = startsWith(pathname, "/coaching-portal/");

  return (
    <aside role="secondary-navbar" tabIndex="-1" className={props.classNames}>
      <h4>{t("settingsNav.general")}</h4>
      <ul>
        {map(PAGES, (data, page) => {
          const fullUrl = `${basePath}${data.url}`;
          const isActive = fullUrl === pathname;

          if (data.group !== "general") return null;

          return (
            <li
              key={page}
              className={cx({
                active: isActive,
              })}
            >
              <a
                className={cx({
                  clipped: isCoachingPortal && isActive,
                  active: !isCoachingPortal && isActive,
                })}
                href={fullUrl}
              >
                {SETTINGS_PAGE_KEYS[page]
                  ? t("settingsNav.pages." + SETTINGS_PAGE_KEYS[page])
                  : page}
              </a>
              <img
                className="settings-arrow only-show-mobile"
                src="/img/svg/icon_arrow-right.svg"
                alt=""
              />
            </li>
          );
        })}
      </ul>
      {(isPullCoach || userIsPerformCoach || isPushCoachWithEndedTrial) &&
        !hideContentForOrganizationsRolesBelowToOwner && (
          <h4>{t("settingsNav.billing")}</h4>
        )}
      <ul>
        {isPullCoachOnTrial || isPushCoachWithEndedTrial ? (
          <a href="/coach-billing" role="button" data-button-type="tertiary">
            <span>{t("settingsNav.upgradeNow")}</span>
          </a>
        ) : (
          <>
            {map(PAGES, (data, page) => {
              const fullUrl = `${basePath}${data.url}`;
              const isActive = fullUrl === pathname;
              const isBetaPage = page === "Manage Coaches";

              const liClassName = cx({
                "mobile-hidden": page === "Edit Profile" || page === "Branding",
                active: isActive,
              });

              if (data.group !== "billing") return null;

              return (
                <li key={page} className={liClassName}>
                  <a
                    className={cx({
                      active: isActive,
                    })}
                    href={fullUrl}
                  >
                    {SETTINGS_PAGE_KEYS[page]
                      ? t("settingsNav.pages." + SETTINGS_PAGE_KEYS[page])
                      : page}
                    {isBetaPage && (
                      <span className="beta-settings-nav">
                        {" "}
                        ({t("settingsNav.beta")})
                      </span>
                    )}
                  </a>
                  <img
                    className="settings-arrow only-show-mobile"
                    src="/img/svg/icon_arrow-right.svg"
                    alt=""
                  />
                </li>
              );
            })}

            {userIsCoach &&
              (isPullCoach || userIsPerformCoach) &&
              !hideContentForOrganizationsRolesBelowToOwner && (
                <details className="invoices-dropdown">
                  <summary>
                    {t("settingsNav.pages.invoices")}
                    <svg
                      className="chevron"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </summary>
                  <div className="invoices-dropdown-content">
                    {isPullCoach && (
                      <a
                        href={`${basePath}settings/coach-billing`}
                        className={cx("invoice-product-row", {
                          active:
                            pathname === `${basePath}settings/coach-billing`,
                        })}
                      >
                        <div className="product-nav-item">
                          <div className="product-nav-icon pull" />
                          <p className="product-nav-name">PULL</p>
                        </div>
                      </a>
                    )}
                    {userIsPerformCoach && (
                      <a
                        href={`${basePath}settings/perform-invoice`}
                        className={cx("invoice-product-row", {
                          active:
                            pathname === `${basePath}settings/perform-invoice`,
                        })}
                      >
                        <div className="product-nav-item">
                          <div className="product-nav-icon perform" />
                          <p className="product-nav-name">PERFORM</p>
                        </div>
                      </a>
                    )}
                  </div>
                </details>
              )}

            {userIsCoach && (
              <li className="h-auto! opacity-100! border-none!">
                <a
                  href={`${basePath}settings/your-products`}
                  className={cx("products-section-link", {
                    active: pathname === `${basePath}settings/your-products`,
                  })}
                >
                  <p className="your-products-title">
                    {t("settingsNav.pages.yourProducts")}
                  </p>

                  {map(PRODUCTS, (product) => (
                    <div key={product.id} className="product-nav-row">
                      <div className="product-nav-item">
                        <div className={`product-nav-icon ${product.id}`} />
                        <p className="product-nav-name">{product.name}</p>
                      </div>
                      <div
                        className={`nav-status-badge ${
                          product.isActive ? "active" : "inactive"
                        }`}
                      >
                        {product.isActive
                          ? t("settingsNav.productActive")
                          : t("settingsNav.productInactive")}
                      </div>
                    </div>
                  ))}
                </a>
              </li>
            )}
          </>
        )}
      </ul>
      {userIsPressCoach && <h4>{t("settingsNav.gym")}</h4>}
      <ul>
        {map(PAGES, (data, page) => {
          const fullUrl = `${basePath}${data.url}`;
          const isActive = fullUrl === pathname;
          const isBetaPage = page === "Manage Coaches";

          const liClassName = cx({
            "mobile-hidden": page === "Edit Profile" || page === "Branding",
            active: isActive,
          });

          if (data.group !== "gym") return null;

          return (
            <li key={page} className={liClassName}>
              <a
                className={cx({
                  active: isActive,
                })}
                href={fullUrl}
              >
                {t("settingsNav.pages." + SETTINGS_PAGE_KEYS[page])}
                {isBetaPage && (
                  <span className="beta-settings-nav"> (BETA)</span>
                )}
              </a>
              <img
                className="settings-arrow only-show-mobile"
                src="/img/svg/icon_arrow-right.svg"
                alt=""
              />
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

export default SettingsNav;